import React, { useMemo } from 'react'
import { SeriesMetaData } from './ChartContent'
import { SsvgChartContent } from './styles'

interface ChartSeriesProps {
	points: ChartPoint[]
	meta: SeriesMetaData | undefined
	xRange: { beg: number | undefined; end: number | undefined }
	yRange: { beg: number | undefined; end: number | undefined }
	color: string
	strokeWidth?: number
	isLog?: boolean
	opacity?: number
}

interface ChartPoint {
	x: number
	y: number
}
const svgDim = 1000
const ChartSeries: React.FC<ChartSeriesProps> = ({ points, meta, xRange, yRange, color, strokeWidth = 1.5, isLog = false, opacity = 1 }) => {
	const pathState = useMemo(() => {
		if (!meta) return ''
		if (xRange?.beg === undefined || xRange?.end === undefined) return ''
		if (yRange?.beg === undefined || yRange?.end === undefined) return ''
		const xRangeLength = meta.end - meta.beg
		if (!isLog) {
			const yRangeLength = meta.max - meta.min
			const pointStrings = points.map((point) => {
				const scaledPoint = {
					x: ((point.x - meta.beg!) / xRangeLength) * svgDim,
					y: (1 - (point.y - meta.min!) / yRangeLength) * svgDim
				}
				return `${scaledPoint.x},${scaledPoint.y}`
			})
			return pointStrings.join(' ')
		} else {
			const minVal = Math.max(meta.min, meta.minPos)
			const base = Math.log(meta.max / minVal)
			const pointStrings: string[] = []
			for (const point of points) {
				if (point.y < minVal) continue
				const scaledPoint = {
					x: ((point.x - meta.beg!) / xRangeLength) * svgDim,
					y: (1 - Math.log(point.y / minVal) / base) * svgDim
				}
				pointStrings.push(`${scaledPoint.x},${scaledPoint.y}`)
			}
			return pointStrings.join(' ')
		}
	}, [points, meta, isLog, xRange, yRange])

	const viewBox = useMemo<[number, number, number, number]>(() => {
		if (yRange?.beg === undefined || yRange?.end === undefined) {
			return [0, 0, svgDim, svgDim]
		}
		if (xRange?.beg === undefined || xRange?.end === undefined) {
			return [0, 0, svgDim, svgDim]
		}
		if (!meta) {
			return [0, 0, svgDim, svgDim]
		}
		const viewBox: [number, number, number, number] = [0, 0, svgDim, svgDim]
		const xRangeLength = xRange.end - xRange.beg
		const yRangeLength = yRange.end - yRange.beg
		const xMetaRange = meta.end - meta.beg
		const yMetaRange = meta.max - meta.min
		viewBox[0] = ((xRange.beg - meta.beg) / xMetaRange) * svgDim
		viewBox[2] = (xRangeLength / xMetaRange) * svgDim
		if (!isLog) {
			viewBox[1] = ((meta.max - yRange.end) / yMetaRange) * svgDim
			viewBox[3] = (yRangeLength / yMetaRange) * svgDim
		} else {
			const minVal = Math.max(meta.min, meta.minPos)
			const base = Math.log(meta.max / minVal)
			viewBox[1] = (-Math.log(yRange.end / meta.max) / base) * svgDim
			viewBox[3] = (-Math.log(yRange.beg / meta.max) / base) * svgDim - viewBox[1]
		}
		return viewBox
	}, [xRange, yRange, isLog, meta])

	return (
		<SsvgChartContent height={svgDim} width={svgDim} viewBox={`${viewBox[0]} ${viewBox[1]} ${viewBox[2]} ${viewBox[3]}`} preserveAspectRatio="none">
			<polyline points={pathState} preserveAspectRatio="none" style={{ fill: 'none', vectorEffect: 'non-scaling-stroke', stroke: color, strokeWidth: strokeWidth, strokeOpacity: opacity }} />
		</SsvgChartContent>
	)
}

export default React.memo(ChartSeries)
